import { normalizePlateSearch } from '../../../utils/plateSearch';
import {
  getTicketCreatedAt,
  getTicketId,
  normalizePlateIndexEntry,
  sortPlateIndexEntriesNewestFirst,
} from '../../../utils/ticketPlateIndex';
import {
  getReportingProxyHeaders,
  getReportingProxyUrl,
} from '../../../utils/reportingClient';

export const VEHICLE_NOTICE_ALERT_THRESHOLD = 5;
export const DEFAULT_VEHICLE_ALERT_REPORTING_API = 'https://maxpark-firebase-sync-worker.onrender.com';

const VEHICLE_ALERT_NOTIFICATION_LIMIT = 5;
const DEFAULT_REPORT_LIMIT = 2000;

const getCleanPublicUrl = publicUrl => {
  if (!publicUrl || publicUrl === '/') {
    return '';
  }

  return publicUrl.replace(/\/$/, '');
};

const cleanUrl = value => String(value || '').trim().replace(/\/+$/, '');

const cleanString = value => String(value || '').trim();

const getReportingApiOrigin = configuredOrigin => cleanUrl(configuredOrigin);

export const getVehicleAlertsReportUrl = (options = {}) => {
  const origin = getReportingApiOrigin(options.reportingApi);
  const params = new URLSearchParams();

  params.set('threshold', String(options.threshold || VEHICLE_NOTICE_ALERT_THRESHOLD));
  params.set('limit', String(options.limit || DEFAULT_REPORT_LIMIT));

  return origin
    ? `${origin}/reports/vehicle-alerts?${params.toString()}`
    : getReportingProxyUrl('vehicle-alerts', params);
};

const normalizeAlertEntry = (entry, fallbackId = '') => {
  const normalizedEntry = normalizePlateIndexEntry(entry, fallbackId);

  return {
    ...entry,
    ...normalizedEntry,
    ticketid: normalizedEntry.ticketid || getTicketId(entry, fallbackId),
    plateNormalized: normalizePlateSearch(normalizedEntry.plateNormalized || normalizedEntry.carplate || entry?.plateNormalized),
  };
};

const getTicketKey = (entry, fallbackIndex) => (
  getTicketId(entry) || `${entry.plateNormalized || entry.carplate || 'ticket'}-${fallbackIndex}`
);

const getBestValue = (tickets, selector) => {
  for (const ticket of tickets) {
    const value = cleanString(selector(ticket));

    if (value) {
      return value;
    }
  }

  return '';
};

const getNumericTimestamp = value => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const normalizeReportTicket = (ticket = {}, alert = {}, fallbackIndex = 0) => normalizeAlertEntry({
  ...ticket,
  carplate: ticket.carplate || ticket.plate || alert.plate,
  plate: ticket.plate || ticket.carplate || alert.plate,
  plateNormalized: ticket.plateNormalized || alert.plateNormalized,
  carbrand: ticket.carbrand || ticket.carmake,
  datetime: ticket.datetime || ticket.date || alert.latestDateLabel,
}, ticket.ticketid || ticket.ticketId || ticket.noticeId || `${alert.plateNormalized || 'ticket'}-${fallbackIndex}`);

const normalizeReportAlert = (alert = {}) => {
  const tickets = Array.isArray(alert.tickets)
    ? alert.tickets.map((ticket, index) => normalizeReportTicket(ticket, alert, index))
    : [];
  const latestTicket = alert.latestTicket
    ? normalizeReportTicket(alert.latestTicket, alert)
    : tickets[0] || {};
  const noticeCount = Number(alert.noticeCount || alert.notice_count || tickets.length || 0);
  const threshold = Number(alert.threshold || VEHICLE_NOTICE_ALERT_THRESHOLD);
  const plateNormalized = normalizePlateSearch(alert.plateNormalized || alert.vehicleKey || alert.id || alert.plate);
  const latestTimestamp =
    Number(alert.latestTimestamp || 0)
    || getTicketCreatedAt(latestTicket)
    || getNumericTimestamp(alert.lastNoticeAt || alert.latestDateLabel);

  return {
    ...alert,
    id: alert.id || plateNormalized,
    plate: cleanString(alert.plate) || plateNormalized,
    plateNormalized,
    noticeCount,
    threshold,
    level: alert.level || (noticeCount >= threshold * 2 ? 'critical' : 'warning'),
    latestTicket,
    latestTicketId: cleanString(alert.latestTicketId || getTicketId(latestTicket)),
    latestTimestamp,
    latestDateLabel: cleanString(alert.latestDateLabel || latestTicket.datetime || alert.lastNoticeAt),
    vehicleDescription: cleanString(alert.vehicleDescription),
    location: cleanString(alert.location || latestTicket.location || latestTicket.contract),
    agent: cleanString(alert.agent || latestTicket.agent),
    tickets,
  };
};

export const summarizeVehicleNoticeAlerts = (entries = [], options = {}) => {
  const threshold = options.threshold || VEHICLE_NOTICE_ALERT_THRESHOLD;
  const groups = new Map();

  entries.forEach((entry, index) => {
    const normalizedEntry = normalizeAlertEntry(entry, getTicketKey(entry, index));
    const plateKey = normalizePlateSearch(
      normalizedEntry.plateNormalized
      || normalizedEntry.carplate
      || normalizedEntry.plate
    );

    if (!plateKey) {
      return;
    }

    const ticketKey = getTicketKey(normalizedEntry, index);
    const group = groups.get(plateKey) || {
      plate: normalizedEntry.carplate || normalizedEntry.plate || plateKey,
      plateNormalized: plateKey,
      tickets: [],
      ticketIds: new Set(),
    };

    if (!group.ticketIds.has(ticketKey)) {
      group.ticketIds.add(ticketKey);
      group.tickets.push({
        ...normalizedEntry,
        plateNormalized: plateKey,
      });
    }

    groups.set(plateKey, group);
  });

  return Array.from(groups.values())
    .map(group => {
      const sortedTickets = sortPlateIndexEntriesNewestFirst(group.tickets);
      const latestTicket = sortedTickets[0] || {};
      const noticeCount = sortedTickets.length;

      return {
        id: group.plateNormalized,
        plate: getBestValue(sortedTickets, ticket => ticket.carplate || ticket.plate) || group.plate,
        plateNormalized: group.plateNormalized,
        noticeCount,
        threshold,
        level: noticeCount >= threshold * 2 ? 'critical' : 'warning',
        latestTicket,
        latestTicketId: getTicketId(latestTicket),
        latestTimestamp: getTicketCreatedAt(latestTicket),
        latestDateLabel: cleanString(latestTicket.datetime)
          || [latestTicket.date, latestTicket.time].map(cleanString).filter(Boolean).join(' '),
        vehicleDescription: [
          getBestValue(sortedTickets, ticket => ticket.carbrand || ticket.make),
          getBestValue(sortedTickets, ticket => ticket.carmodel || ticket.model),
          getBestValue(sortedTickets, ticket => ticket.carcolor || ticket.color),
        ].filter(Boolean).join(' '),
        location: getBestValue(sortedTickets, ticket => (
          ticket.contractname
          || ticket.clientname
          || ticket.location
          || ticket.address
          || ticket.contract
        )),
        agent: getBestValue(sortedTickets, ticket => (
          ticket.agent
          || [ticket.agentfirstname, ticket.agentlastname].map(cleanString).filter(Boolean).join(' ')
        )),
        tickets: sortedTickets.slice(0, 8),
      };
    })
    .filter(alert => alert.noticeCount >= threshold)
    .sort((left, right) => (
      right.noticeCount - left.noticeCount
      || right.latestTimestamp - left.latestTimestamp
      || left.plateNormalized.localeCompare(right.plateNormalized)
    ));
};

export const loadVehicleNoticeAlerts = async (options = {}) => {
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);

  if (!fetchImpl) {
    throw new Error('Impossible de charger les alertes vehicules: fetch non disponible.');
  }

  const threshold = options.threshold || VEHICLE_NOTICE_ALERT_THRESHOLD;
  const headers = options.reportingApi
    ? { Accept: 'application/json' }
    : await getReportingProxyHeaders();
  const response = await fetchImpl(getVehicleAlertsReportUrl({
    threshold,
    limit: options.limit,
    reportingApi: options.reportingApi,
  }), {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Impossible de charger les alertes vehicules (${response.status}).`);
  }

  const report = await response.json();
  const alerts = Array.isArray(report.alerts)
    ? report.alerts.map(normalizeReportAlert)
    : [];

  return {
    ...report,
    alerts,
    threshold: Number(report.threshold || threshold),
    loadedSources: Array.isArray(report.loadedSources) && report.loadedSources.length
      ? report.loadedSources
      : ['render-postgres'],
    errors: Array.isArray(report.errors) ? report.errors : [],
    totalEntries: Number(report.totalEntries || alerts.reduce((total, alert) => total + alert.noticeCount, 0)),
    totalVehicles: Number(report.totalVehicles || alerts.length),
    loadedAt: report.loadedAt || new Date().toISOString(),
  };
};

export const getVehicleAlertsPath = (publicUrl = '') => `${getCleanPublicUrl(publicUrl)}/dashboard/alertes-vehicules`;

export const buildVehicleAlertNotificationItems = (alerts = [], options = {}) => {
  const limit = options.limit || VEHICLE_ALERT_NOTIFICATION_LIMIT;
  const publicUrl = options.publicUrl || '';
  const basePath = getVehicleAlertsPath(publicUrl);

  return (Array.isArray(alerts) ? alerts : [])
    .slice()
    .sort((left, right) => (
      (Number(right.latestTimestamp) || 0) - (Number(left.latestTimestamp) || 0)
      || right.noticeCount - left.noticeCount
      || left.plateNormalized.localeCompare(right.plateNormalized)
    ))
    .slice(0, limit)
    .map(alert => ({
      id: `vehicle-alert-${alert.plateNormalized}`,
      ticketNumber: alert.latestTicketId || '',
      title: 'Alerte vehicule',
      description: `${alert.plate}: ${alert.noticeCount} avis`,
      statusLabel: `${alert.threshold || VEHICLE_NOTICE_ALERT_THRESHOLD} avis ou plus`,
      timestamp: alert.latestTimestamp,
      fallbackTime: alert.latestDateLabel,
      path: `${basePath}?plate=${encodeURIComponent(alert.plateNormalized)}`,
      variant: 'danger',
    }));
};
